import { Link } from 'react-router-dom'
import { paths } from 'src/routes/paths'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { PolicyPage, PolicySection } from './PolicyPage'

export const PrivacyPolicy = () => {
  const { t } = useLocalization()

  return (
    <PolicyPage title={t('Privacy policy', 'Integritetspolicy')}>
      <PolicySection title={t('1. Controller', '1. Personuppgiftsansvarig')}>
        <p>
          {t(
            'Klayder AB, Svandammsvägen 36, 126 34 Hägersten, Sweden (org. no. 559433-8104) is the controller of personal data processed through klayder.com and app.justmerch.co. Contact: carmelo@klayder.com.',
            'Klayder AB, Svandammsvägen 36, 126 34 Hägersten, Sverige (org.nr 559433-8104) är personuppgiftsansvarig för personuppgifter som behandlas via klayder.com och app.justmerch.co. Kontakt: carmelo@klayder.com.'
          )}
        </p>
      </PolicySection>

      <PolicySection
        title={t('2. What we collect', '2. Vilka uppgifter vi samlar in')}
      >
        <p>
          {t(
            'Depending on how you use Klayder, we may process:',
            'Beroende på hur du använder Klayder kan vi behandla:'
          )}
        </p>
        <ul>
          <li>
            {t(
              'Identity and contact: name, work email, phone, job title',
              'Identitet och kontakt: namn, arbetsmejl, telefon, titel'
            )}
          </li>
          <li>
            {t(
              'Organisation and billing: company name, address, org. no., VAT no., invoice email',
              'Organisation och fakturering: företagsnamn, adress, org.nr, momsnr, fakturamejl'
            )}
          </li>
          <li>
            {t(
              'Account: login identifiers from magic link or Google / Microsoft work-account sign-in',
              'Konto: inloggningsuppgifter från magisk länk eller Google- / Microsoft-jobbkonto'
            )}
          </li>
          <li>
            {t(
              'Order data: quotes, products, sizes, decorations, proofs, invoices, delivery details',
              'Orderdata: offerter, produkter, storlekar, dekorationer, korrekturer, fakturor, leveransuppgifter'
            )}
          </li>
          <li>
            {t(
              'Artwork and chat: logos, files, messages with Sara or the Klayder team, and generated mockups',
              'Original och chatt: logotyper, filer, meddelanden med Sara eller Klayder-teamet, och genererade mockups'
            )}
          </li>
          <li>
            {t(
              'Technical data: IP address, device/browser, and session information needed to run the service',
              'Tekniska uppgifter: IP-adress, enhet/webbläsare och sessionsinformation som behövs för att köra tjänsten'
            )}
          </li>
        </ul>
      </PolicySection>

      <PolicySection
        title={t('3. How we collect it', '3. Hur vi samlar in uppgifterna')}
      >
        <p>
          {t(
            'We collect data when you register, sign in, fill in organisation details, build or approve a quote, upload artwork, chat with Sara or our team, contact us, or browse the public site. We may also fetch a company logo from Brandfetch when you enter a work email, and receive identity details from Google or Microsoft if you sign in that way.',
            'Vi samlar in uppgifter när du registrerar dig, loggar in, fyller i organisationsuppgifter, bygger eller godkänner en offert, laddar upp original, chattar med Sara eller vårt team, kontaktar oss eller besöker den öppna sajten. Vi kan också hämta en företagslogotyp från Brandfetch när du anger en arbetsmejl, och ta emot identitetsuppgifter från Google eller Microsoft om du loggar in på det sättet.'
          )}
        </p>
      </PolicySection>

      <PolicySection
        title={t('4. Why we use it', '4. Varför vi använder uppgifterna')}
      >
        <p>
          {t(
            'We process personal data to:',
            'Vi behandlar personuppgifter för att:'
          )}
        </p>
        <ul>
          <li>
            {t(
              'Create and administer your account and organisation (contract)',
              'Skapa och administrera ditt konto och din organisation (avtal)'
            )}
          </li>
          <li>
            {t(
              'Quote, produce, deliver, and invoice merch orders (contract)',
              'Offerera, producera, leverera och fakturera merchordrar (avtal)'
            )}
          </li>
          <li>
            {t(
              'Run Sara and team chat, including mockups from your artwork (contract / legitimate interest)',
              'Köra Sara och teamchatt, inklusive mockups från dina original (avtal / berättigat intresse)'
            )}
          </li>
          <li>
            {t(
              'Keep the platform secure and prevent abuse (legitimate interest)',
              'Hålla plattformen säker och förhindra missbruk (berättigat intresse)'
            )}
          </li>
          <li>
            {t(
              'Meet bookkeeping, tax, and other legal duties (legal obligation)',
              'Uppfylla bokförings-, skatte- och andra lagkrav (rättslig förpliktelse)'
            )}
          </li>
          <li>
            {t(
              'Respond to sales and support messages (contract / legitimate interest)',
              'Svara på sälj- och supportmeddelanden (avtal / berättigat intresse)'
            )}
          </li>
        </ul>
      </PolicySection>

      <PolicySection
        title={t('5. Who we share it with', '5. Vem vi delar med')}
      >
        <p>
          {t(
            'We share data only as needed to run Klayder:',
            'Vi delar uppgifter endast när det behövs för att driva Klayder:'
          )}
        </p>
        <ul>
          <li>
            {t(
              'Supabase — hosting, authentication, database, and file storage',
              'Supabase — hosting, autentisering, databas och fillagring'
            )}
          </li>
          <li>
            {t(
              'OpenAI, Groq, and Google — to run Sara (chat, search intent, and mockups)',
              'OpenAI, Groq och Google — för att köra Sara (chatt, sökintent och mockups)'
            )}
          </li>
          <li>
            {t(
              'Brandfetch — company logo lookup from a work-email domain',
              'Brandfetch — uppslag av företagslogotyp från arbetsmejldomän'
            )}
          </li>
          <li>
            {t(
              'Factories, decoration partners, and carriers — to produce and ship the order',
              'Fabriker, dekorationspartners och transportörer — för att producera och skicka ordern'
            )}
          </li>
          <li>
            {t(
              'Email and operations tools — to send magic links, invoices, and order updates',
              'Mejl- och driftverktyg — för magiska länkar, fakturor och orderuppdateringar'
            )}
          </li>
        </ul>
        <p>
          {t(
            'We may also disclose data if required by law, to protect Klayder or others, or if we sell or merge the business (the buyer would then be bound by this policy or an equivalent one). We do not sell personal data.',
            'Vi kan också lämna ut uppgifter om lagen kräver det, för att skydda Klayder eller andra, eller om vi säljer eller fusionerar verksamheten (köparen binds då av denna policy eller en likvärdig). Vi säljer inte personuppgifter.'
          )}
        </p>
      </PolicySection>

      <PolicySection
        title={t('6. Transfers outside the EU/EEA', '6. Överföringar utanför EU/EES')}
      >
        <p>
          {t(
            'Some processors (including AI providers) may process data in the United States or other countries outside the EU/EEA. Where that happens we use appropriate safeguards, such as the European Commission’s standard contractual clauses, and only transfer what is needed for the service.',
            'Vissa personuppgiftsbiträden (inklusive AI-leverantörer) kan behandla uppgifter i USA eller andra länder utanför EU/EES. Då använder vi lämpliga skyddsåtgärder, till exempel EU-kommissionens standardavtalsklausuler, och överför bara det som behövs för tjänsten.'
          )}
        </p>
      </PolicySection>

      <PolicySection title={t('7. Retention', '7. Lagringstid')}>
        <p>
          {t(
            'Account and organisation data are kept while the account is active. Order, invoice, and bookkeeping records are kept for as long as Swedish accounting and tax law requires (generally seven years). Chat, artwork, and mockups are kept for as long as needed to fulfil and support the order, then deleted or anonymised when they are no longer needed, unless a longer period is required by law or to handle a dispute.',
            'Konto- och organisationsuppgifter sparas så länge kontot är aktivt. Order-, faktura- och bokföringsuppgifter sparas så länge svensk bokförings- och skattelag kräver (i regel sju år). Chatt, original och mockups sparas så länge det behövs för att genomföra och supporta ordern, och raderas eller anonymiseras därefter, om inte en längre tid krävs enligt lag eller för att hantera en tvist.'
          )}
        </p>
        <p>
          {t(
            'Anonymous draft quotes that never become a real organisation expire after a short period and are deleted.',
            'Anonyma offertutkast som aldrig blir en riktig organisation upphör efter en kort tid och raderas.'
          )}
        </p>
      </PolicySection>

      <PolicySection title={t('8. Your rights', '8. Dina rättigheter')}>
        <p>
          {t(
            'Under GDPR you may request access, rectification, erasure, restriction, objection, or data portability, where those rights apply. You may also withdraw consent where processing is based on consent. To exercise these rights, email carmelo@klayder.com. We will respond according to applicable law.',
            'Enligt GDPR kan du begära tillgång, rättelse, radering, begränsning, invändning eller dataportabilitet, där de rättigheterna gäller. Du kan också återkalla samtycke när behandlingen grundas på samtycke. För att utöva rättigheterna, mejla carmelo@klayder.com. Vi svarar enligt tillämplig lag.'
          )}
        </p>
        <p>
          {t(
            'You can lodge a complaint with the Swedish Authority for Privacy Protection (IMY), imy.se.',
            'Du kan klaga till Integritetsskyddsmyndigheten (IMY), imy.se.'
          )}
        </p>
        <p>
          {t(
            'If we need certain data to provide the service and you object to that processing, we may not be able to keep providing the account or complete an order.',
            'Om vi behöver vissa uppgifter för att tillhandahålla tjänsten och du invänder mot den behandlingen kanske vi inte kan behålla kontot eller slutföra en order.'
          )}
        </p>
      </PolicySection>

      <PolicySection title={t('9. Security', '9. Säkerhet')}>
        <p>
          {t(
            'We use reasonable technical and organisational measures to protect personal data. No online service is completely secure; you also share that responsibility by keeping your login and devices safe.',
            'Vi använder skäliga tekniska och organisatoriska åtgärder för att skydda personuppgifter. Ingen onlinetjänst är helt säker; du delar ansvaret genom att hålla inloggning och enheter säkra.'
          )}
        </p>
      </PolicySection>

      <PolicySection title={t('10. Cookies', '10. Cookies')}>
        <p>
          {t(
            'See our ',
            'Se vår '
          )}
          <Link to={paths.cookieConsent}>
            {t('cookie policy', 'cookiepolicy')}
          </Link>
          {t(
            ' for how we use cookies and similar technologies.',
            ' för hur vi använder cookies och liknande tekniker.'
          )}
        </p>
      </PolicySection>

      <PolicySection title={t('11. Children', '11. Barn')}>
        <p>
          {t(
            'Klayder is a business platform and is not directed at children.',
            'Klayder är en företagsplattform och riktar sig inte till barn.'
          )}
        </p>
      </PolicySection>

      <PolicySection title={t('12. Changes', '12. Ändringar')}>
        <p>
          {t(
            'We may update this policy. The “Last updated” date at the top will change. Continued use of the service after an update means you have read the new version. If changes are material, we will take reasonable steps to let account holders know.',
            'Vi kan uppdatera den här policyn. Datumet ”Senast uppdaterad” högst upp ändras då. Fortsatt användning av tjänsten efter en uppdatering innebär att du har tagit del av den nya versionen. Vid väsentliga ändringar tar vi skäliga steg för att meddela kontoinnehavare.'
          )}
        </p>
      </PolicySection>
    </PolicyPage>
  )
}
